import { labelMatcher } from './label-mapper';

const buildDataObject = (entryValue, stats) => {
  return entryValue !== null
    ? [
        {
          type: 'main',
          value: entryValue,
          ...stats.classificationMinMax,
        },
        {
          type: 'median',
          area: 'entityType',
          min: stats.classificationMinMax.min,
          max: stats.classificationMinMax.max,
          ...stats.classificationMedian,
        },

        { type: 'text', ...stats.stateMinAndMax },
      ]
    : {};
};

const buildDataComparisonObject = (entryValue, stats) => {
  const globalScale = stats.classificationMinMax;
  return [
    {
      type: 'main',
      value: entryValue,
      ...globalScale,
    },
    {
      type: 'compared',
      legendHidden: true,
      value: stats.valueToCompare,
      ...globalScale,
    },
    { type: 'text', ...stats.classificationMinMax },
  ];
};

const getStats = (
  councilData,
  entryAttribute,
  subEntryAttribute,
  subCategories
) => {
  const classificationMinMax = getMinMax(
    councilData.classification,
    entryAttribute,
    subEntryAttribute,
    subCategories
  );
  const stateMinAndMax = getMinMax(
    councilData.state,
    entryAttribute,
    subEntryAttribute,
    subCategories
  );

  const classificationMedian = getMedian(
    councilData.classification,
    entryAttribute,
    subEntryAttribute
  );

  return {
    classificationMinMax,
    stateMinAndMax,
    classificationMedian,
  };
};

const getComparisonStats = (
  councilData,
  entryAttribute,
  subEntryAttribute,
  subCategories
) => {
  const classificationMinMax = getMinMax(
    councilData.classification,
    entryAttribute,
    subEntryAttribute,
    subCategories
  );

  const stateMinAndMax = getMinMax(
    councilData.state,
    entryAttribute,
    subEntryAttribute,
    subCategories
  );

  const valueToCompare = subEntryAttribute
    ? councilData.comparedEntity[entryAttribute][subEntryAttribute]
    : councilData.comparedEntity[entryAttribute];

  return {
    classificationMinMax,
    stateMinAndMax,
    valueToCompare,
  };
};

const getMedian = (dataArray, entryAttribute, subEntryAttribute) => {
  const dataArrayByAttribute = dataArray.map((el) => {
    return subEntryAttribute
      ? el[entryAttribute][subEntryAttribute]
      : el[entryAttribute];
  });
  return computeMedian(dataArrayByAttribute);
};

const getMinMax = (
  dataArray,
  entryAttribute,
  subEntryAttribute,
  subCategories
) => {
  let min = null,
    max = null;
  const isTextLookup = {
    High: true,
    Moderate: true,
    Lower: true,
  };

  // <dan>
  const filteredArray = dataArray.filter((elem) => elem !== undefined);

  const firstCouncil = filteredArray[0];
  const firstValue = subEntryAttribute
    ? firstCouncil[entryAttribute][subEntryAttribute]
    : firstCouncil[entryAttribute];
  if (isTextLookup[firstValue]) {
    return {
      min: 'Lower',
      max: 'High',
      trueMin: 'Lower',
    };
  }

  const arrayToCompare = filteredArray
    .map((entity) => {
      return subEntryAttribute
        ? entity[entryAttribute][subEntryAttribute]
        : entity[entryAttribute];
    })
    .filter((elem) => !isNaN(elem) && elem !== null);

  max = Math.max(...arrayToCompare);
  min = Math.min(...arrayToCompare);

  // Overriding default max values with unique requirements for calculation
  if (entryAttribute === 'entityCapacity') {
    max = 1;
  } else if (entryAttribute === 'facilitiesPerEntity') {
    const entityFacilities = subCategories.map((x) => x.value);
    const allFacilities = filteredArray.map((x) => x.facilitiesPerEntity);
    const allFacilitiesCapacity = allFacilities.reduce((acc, curr) => {
      return (
        acc +
        Object.keys(curr).reduce(function (acc, key) {
          if (entityFacilities.indexOf(key) >= 0 && key !== 'Total') {
            return acc + curr[key];
          }
          return acc;
        }, 0)
      );
    }, 0);
    max = allFacilitiesCapacity;
  }

  return {
    min: Math.min(0, min),
    max: max,
    trueMin: min,
  };
};

export const dataComparisonMapper = (councilData) => {
  return Object.entries(councilData.activeEntity).map(
    ([entryAttribute, entryValue]) => {
      var data = {};
      var subCategories = null;
      if (entryValue !== null && typeof entryValue === 'object') {
        subCategories = Object.keys(entryValue).map((entryName) => {
          return {
            value: entryName,
            label: labelMatcher(entryName),
          };
        });

        Object.entries(entryValue).forEach(
          ([subEntryAttribute, subEntryValue]) => {
            const stats = getComparisonStats(
              councilData,
              entryAttribute,
              subEntryAttribute,
              subCategories
            );
            data[subEntryAttribute] = buildDataComparisonObject(
              subEntryValue,
              stats
            );
          }
        );
      } else {
        const stats = getComparisonStats(councilData, entryAttribute);
        data = buildDataComparisonObject(entryValue, stats);
      }

      return {
        title: labelMatcher(entryAttribute),
        subCategories: subCategories,
        key: entryAttribute,
        data: data,
      };
    }
  );
};

export const dataMapper = (entityData) => {
  const { activeEntity } = entityData;

  // console.log('AE', activeEntity);

  //For every attributes of the councilData object
  return Object.entries(activeEntity).map(([entryAttribute, entryValue]) => {
    var data = {};
    var subCategories = null;
    //if there is a sub object
    // eg: rateAndAnnualCharges: {
    //   total: ...,
    //   ordinaryRates: ...
    //   etc...
    // }
    if (typeof entryValue === 'object' && entryValue !== null) {
      //SubCategory array is filled with the sub object attributes so we can pick them in a select dropdown
      //eg: [
      //  {value: "total", label: "total"},
      //  {value: "ordinaryRates", label: "ordinary rates"}
      // ]

      // Move Total to the top of the list
      var firstInList = 'Total';
      const sortedEntryValues = Object.keys(entryValue).sort(function (x, y) {
        return x === firstInList ? -1 : y === firstInList ? 1 : 0;
      });
      subCategories = sortedEntryValues.map((entryName) => {
        return {
          value: entryName,
          label: labelMatcher(entryName),
        };
      });

      // Data is an object with attributes = subcategories
      Object.entries(entryValue).forEach(
        ([subEntryAttribute, subEntryValue]) => {
          const stats = getStats(
            entityData,
            entryAttribute,
            subEntryAttribute,
            subCategories
          );
          data[subEntryAttribute] = buildDataObject(subEntryValue, stats);
        }
      );
    } else {
      const stats = getStats(entityData, entryAttribute, null);
      data = buildDataObject(entryValue, stats);
    }

    const ret = {
      title: labelMatcher(entryAttribute),
      subCategories: subCategories,
      key: entryAttribute,
      data: data,
    };
    return ret;
  });
};

const computeMedian = (array) => {
  const sortedArray = array.slice().sort(sortNumber);
  var median = 0;
  if (sortedArray.length % 2 === 0) {
    median =
      (sortedArray[sortedArray.length / 2] +
        sortedArray[sortedArray.length / 2 - 1]) /
      2;
  } else {
    median = sortedArray[(sortedArray.length - 1) / 2];
  }

  return {
    value: median,
  };
};

const sortNumber = (a, b) => {
  return a - b;
};



// WEBPACK FOOTER //
// ./src/helpers/data-mapper.js